"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveConfig = resolveConfig;
exports.publicRequest = publicRequest;
exports.buildQuery = buildQuery;
const errors_js_1 = require("./errors.js");
const DEFAULT_BASE_URL = "http://localhost:5001";
const DEFAULT_TIMEOUT_MS = 30000;
function resolveBaseUrl(baseUrl) {
    if (baseUrl !== undefined) {
        const trimmed = String(baseUrl).trim();
        return trimmed === "" ? "" : trimmed.replace(/\/$/, "");
    }
    return DEFAULT_BASE_URL;
}
function resolveConfig(options = {}) {
    const headers = {
        Accept: "application/json",
        ...(options.headers || {}),
    };
    if (options.apiKey) {
        headers.Authorization = `Bearer ${options.apiKey}`;
        headers["X-Midpoint-API-Key"] = options.apiKey;
        headers["X-Client-View-Api-Key"] = options.apiKey;
    }
    return {
        baseUrl: resolveBaseUrl(options.baseUrl),
        timeoutMs: options.timeoutMs ?? DEFAULT_TIMEOUT_MS,
        signal: options.signal,
        headers,
    };
}
function assertPublicPath(path) {
    if (path.includes("/api/admin")) {
        throw new Error("Client View SDK must not call admin API paths.");
    }
}
async function publicRequest(path, options = {}) {
    assertPublicPath(path);
    const config = resolveConfig(options);
    const method = (options.method || "GET").toUpperCase();
    const headers = { ...config.headers };
    let body;
    if (options.body != null) {
        headers["Content-Type"] = "application/json";
        body = JSON.stringify(options.body);
    }
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), config.timeoutMs);
    if (config.signal) {
        if (config.signal.aborted) {
            controller.abort();
        }
        else {
            config.signal.addEventListener("abort", () => controller.abort(), { once: true });
        }
    }
    const signal = controller.signal;
    try {
        const response = await fetch(`${config.baseUrl}${path}`, {
            method,
            headers,
            body,
            credentials: options.credentials ?? "omit",
            signal,
        });
        let parsed = null;
        try {
            parsed = (await response.json());
        }
        catch {
            parsed = null;
        }
        if (!response.ok) {
            if (response.status === 429) {
                const retryAfter = response.headers.get("Retry-After");
                const retryAfterSeconds = retryAfter ? Number(retryAfter) : null;
                throw new errors_js_1.RateLimitError(parsed?.message || "Too many requests.", Number.isFinite(retryAfterSeconds) ? retryAfterSeconds : null, parsed);
            }
            throw (0, errors_js_1.normalizeApiError)(response.status, parsed, `Request failed (${response.status})`);
        }
        return parsed;
    }
    catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
            throw new errors_js_1.NetworkError("Request was aborted or timed out.");
        }
        if (error instanceof Error &&
            !error.status &&
            error.name !== "ClientViewSdkError") {
            throw new errors_js_1.NetworkError(error.message || "Network request failed.");
        }
        throw error;
    }
    finally {
        clearTimeout(timeout);
    }
}
function buildQuery(params = {}) {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
            search.set(key, String(value));
        }
    });
    const query = search.toString();
    return query ? `?${query}` : "";
}
